import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { AuthProvider, ProtectedRoute, AdminRoute } from "@/components/shared/AuthProvider";
import { trackAppLoaded } from "@/lib/firebase";
import { UnifiedStatusProvider } from "@/contexts/UnifiedStatusContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ThemeBridge } from "@/components/shared/ThemeBridge";
import { ConceptsPage } from "@/components/concepts/ConceptsPage";
import { Toaster } from "sonner";
import { ErrorBrainProvider, useErrorBrain } from "@/contexts/ErrorBrainContext";
import { ErrorBrainBoundary } from "@/components/shared/ErrorBrainBoundary";
import ErrorToast from "@/components/shared/ErrorToast";
import { OfflineProvider } from "@/contexts/OfflineContext";
import OfflineBanner from "@/components/shared/OfflineBanner";
import { setErrorBrainHook } from "@/api";
import FlagRoute from "@/components/shared/FlagRoute";
import ScrollRestore from "@/components/shared/ScrollRestore";
import { VendorDataProvider } from "@/contexts/VendorDataContext";
import { VendorModeProvider, useVendorMode } from "@/contexts/VendorModeContext";
import { useAuth } from "@/contexts/AuthContext";

import OnboardingPage from "@/pages/OnboardingPage";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordPages";
import HomePage from "@/pages/HomePage";
import CountdownPage from "@/pages/CountdownPage";
import OfferDetailPage from "@/pages/OfferDetailPage";
import BirthdayOfferDetailPage from "@/pages/BirthdayOfferDetailPage";
import FullscreenCoverageMap from "@/pages/FullscreenCoverageMap";
import FoodTruckDetail from "@/pages/FoodTruckDetail";
import MyDealsPage from "@/pages/MyDealsPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import ProfilePage from "@/pages/ProfilePage";
import { VerificationHistoryPage, PointsHistoryPage } from "@/pages/HistoryPages";
import FeedbackPage from "@/pages/FeedbackPage";
import AdminPage from "@/pages/AdminPage";
import DecisionLogTablePage from "@/pages/DecisionLogTablePage";
import TipsPage from "@/pages/TipsPage";
import OnboardingDemoPage from "@/pages/OnboardingDemoPage";
import UserAnalyticsPage from "@/pages/UserAnalyticsPage";
import WeatherReportPage from "@/pages/WeatherReportPage";
import WelcomePage from "@/pages/WelcomePage";
import ForkScreen from "@/pages/ForkScreen";
import UserOnboarding from "@/pages/UserOnboarding";
import VendorOnboarding from "@/pages/VendorOnboarding";
import DisclaimerPage from "@/pages/DisclaimerPage";
import EthicalPicksPage from "@/pages/EthicalPicksPage";
import TrendingMapPage from "@/pages/TrendingMapPage";
import SubmitOfferPage from "@/pages/SubmitOfferPage";
import MySubmissionsPage from "@/pages/MySubmissionsPage";
import PlanBirthdayPage from "@/pages/PlanBirthdayPage";
import BirthdayPlanViewPage from "@/pages/BirthdayPlanViewPage";
import ItineraryPage from "@/pages/ItineraryPage";
import SharedItineraryPage from "@/pages/SharedItineraryPage";
import AnnualReportPage from "@/pages/AnnualReportPage";
import DecisionEnginePage from "@/pages/DecisionEnginePage";
import WishlistPage from "@/pages/WishlistPage";
import SharedWishlistPage from "@/pages/SharedWishlistPage";
import WhatsNewPage from "@/pages/WhatsNewPage";
import CityDealsPage from "@/pages/CityDealsPage";
import VerificationsPage from "@/pages/VerificationsPage";
import EarnPointsPage from "@/pages/EarnPointsPage";
import BirthdaySummaryPage from "@/pages/BirthdaySummaryPage";
import FriendCountdownPage from "@/pages/FriendCountdownPage";
import BirthdayCrawlPlanner from "@/pages/BirthdayCrawlPlanner";
import TruckLeaderboardPage from "@/pages/TruckLeaderboardPage";
import LiveTruckMap from "@/pages/LiveTruckMap";
import BirthdayWrapped from "@/pages/BirthdayWrapped";
import TruckOrderPage from "@/pages/TruckOrderPage";
import { AppTour } from "@/components/shared/AppTour";
import AppNpsSurvey from "@/components/AppNpsSurvey";
import DevBadge from "@/components/shared/DevBadge";
import { OnboardingProvider } from "@/contexts/OnboardingContext";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsPage from "@/pages/TermsPage";
import AboutPage from "@/pages/AboutPage";
import InstallPromptBanner from "@/components/shared/InstallPromptBanner";
import AgeUnlockScreen from "@/pages/AgeUnlockScreen";
import DiscoverPage from "@/pages/DiscoverPage";
import MyBirthdayPage from "@/pages/MyBirthdayPage";
import ActivityPage from "@/pages/ActivityPage";
import WantedPromosPage from "@/pages/WantedPromosPage";
import SpotTruckPage from "@/pages/SpotTruckPage";
import ReportUpdatePage from "@/pages/ReportUpdatePage";
import BetaPanelPage from "@/pages/BetaPanelPage";
import PostBirthdaySurveyBottomSheet from "@/components/PostBirthdaySurveyBottomSheet";
import FirstBadgeCelebration from "@/components/FirstBadgeCelebration";
import BetaBroadcastCard from "@/components/BetaBroadcastCard";
import BetaFeedbackFAB from "@/components/BetaFeedbackFAB";

// Vendor mode imports
import ModeSelector from "@/components/vendor/ModeSelector";
import ModeSwitcher from "@/components/vendor/ModeSwitcher";
import { PendingVendorBanner } from "@/components/vendor/VendorRoute";
import { VendorRoute } from "@/components/vendor/VendorRoute";
import VendorLayout from "@/components/vendor/VendorLayout";
import VendorDashboard from "@/pages/vendor/VendorDashboard";
import VendorAnalytics from "@/pages/vendor/VendorAnalytics";
import VendorGoLive from "@/pages/vendor/VendorGoLive";
import VendorQRCode from "@/pages/vendor/VendorQRCode";
import VendorSettings from "@/pages/vendor/VendorSettings";
import VendorPlan from "@/pages/vendor/VendorPlan";
import { lazy, Suspense } from "react";

const VendorTruckInfo = lazy(() => import("@/pages/vendor/VendorTruckInfo"));
const VendorBirthdayCard = lazy(() => import("@/pages/vendor/VendorBirthdayCard"));
const VendorPhotos = lazy(() => import("@/pages/vendor/VendorPhotos"));
const VendorMenu = lazy(() => import("@/pages/vendor/VendorMenu"));
const VendorSocialMedia = lazy(() => import("@/pages/vendor/VendorSocialMedia"));
const VendorProfilePreview = lazy(() => import("@/pages/vendor/VendorProfilePreview"));
const VendorPOSSelection = lazy(() => import("@/pages/vendor/VendorPOSSelection"));
const VendorPortfolio = lazy(() => import("@/pages/vendor/VendorPortfolio"));
const VendorMenuScan = lazy(() => import("@/pages/vendor/VendorMenuScan"));
const VendorOrders = lazy(() => import("@/pages/vendor/VendorOrders"));
const KitchenDisplay = lazy(() => import("@/pages/vendor/KitchenDisplay"));
const VendorPunchCard = lazy(() => import("@/pages/vendor/VendorPunchCard"));
const VendorHolidayCampaigns = lazy(() => import("@/pages/vendor/VendorHolidayCampaigns"));
const VendorSchedulePlanner = lazy(() => import("@/pages/vendor/VendorSchedulePlanner"));
const VendorAchievements = lazy(() => import("@/pages/vendor/VendorAchievements"));
const VendorReviews = lazy(() => import("@/pages/vendor/VendorReviews"));
const BirthdayBuddy = lazy(() => import("@/pages/friends/BirthdayBuddy"));
const FriendCrawlPlanner = lazy(() => import("@/pages/friends/FriendCrawlPlanner"));
const MyReferrals = lazy(() => import("@/pages/friends/MyReferrals"));
const BirthdayWrappedHistory = lazy(() => import("@/pages/BirthdayWrappedHistory"));

import "./App.css";

import { SSEProvider } from "@/contexts/SSEContext";
import { UserStateProvider, useUserState } from "@/contexts/UserStateContext";
import { trackAppOpened } from "@/lib/events";

function App() {
  useEffect(() => { trackAppLoaded(); }, []);
  return (
    <ErrorBrainProvider>
    <AuthProvider>
      <SSEProvider>
      <VendorModeProvider>
      <VendorDataProvider>
      <OnboardingProvider>
      <ThemeBridge>
      <UnifiedStatusProvider>
      <UserStateProvider>
      <AppInner />
      </UserStateProvider>
      </UnifiedStatusProvider>
      </ThemeBridge>
      </OnboardingProvider>
      </VendorDataProvider>
      </VendorModeProvider>
      </SSEProvider>
    </AuthProvider>
    </ErrorBrainProvider>
  );
}

function AppInner() {
  const { showError } = useErrorBrain();
  const { user, loading: authLoading } = useAuth();
  const { vendorMode, isVerified, isPending, modeSelected, loading: vendorLoading } = useVendorMode();
  const { state: userBdayState, days_until_birthday, loading: userStateLoading } = useUserState();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const appOpenedFiredRef = useRef(false);

  // v1.0.0 GATE 3 — Fire app_opened once per session after user state resolves
  useEffect(() => {
    if (appOpenedFiredRef.current) return;
    if (authLoading || userStateLoading) return;
    appOpenedFiredRef.current = true;
    trackAppOpened(userBdayState || "unknown", days_until_birthday);
  }, [authLoading, userStateLoading, userBdayState, days_until_birthday]);

  // Wire Axios interceptor to ErrorBrain context
  useEffect(() => {
    setErrorBrainHook(showError);
  }, [showError]);

  // Global unhandled promise rejection handler
  useEffect(() => {
    const handler = (event) => {
      event.preventDefault();
      showError("UNKNOWN_ERROR", { endpoint: window.location.pathname });
    };
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, [showError]);

  // iter270.5 — Cost Tracker v2.1 session heartbeat. Pings every 60s
  // while the tab is visible so DAU is measured by *engagement*, not
  // by login alone. Pauses on hidden tabs to avoid background noise.
  useEffect(() => {
    if (!user) return;
    const ping = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/session/heartbeat`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            keepalive: true,
          }
        );
      } catch (_) {
        // never throw — tracking is best-effort
      }
    };
    ping();
    const id = setInterval(ping, 60000);
    return () => clearInterval(id);
  }, [user]);

  // Wait for auth and vendor mode to load before rendering any routes
  if (authLoading || vendorLoading) {
    return (
      <div className="max-w-md mx-auto min-h-screen shadow-2xl relative flex items-center justify-center" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }}>
        <div className="w-8 h-8 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show mode selector for verified vendors who haven't selected yet
  // BUT only if onboarding is complete — during onboarding, let ForkScreen/VendorOnboarding handle it
  if (user && isVerified && !modeSelected && user.onboarding_complete) {
    return (
      <div className="max-w-md mx-auto min-h-screen shadow-2xl relative">
        <ModeSelector />
      </div>
    );
  }

  // If vendor mode is active, render vendor shell (only after onboarding is done)
  if (user && vendorMode && isVerified && user.onboarding_complete) {
    return (
      <OfflineProvider>
      <ErrorBrainBoundary onError={showError}>
      <div className="max-w-md mx-auto min-h-screen shadow-2xl relative bg-[#1C1C1E]">
        <BrowserRouter>
          <ScrollRestore />
          <Routes>
            <Route element={<VendorRoute><VendorLayout /></VendorRoute>}>
              <Route path="/vendor" element={<VendorDashboard />} />
              <Route path="/vendor/analytics" element={<VendorAnalytics />} />
              <Route path="/vendor/go-live" element={<VendorGoLive />} />
              <Route path="/vendor/qr" element={<VendorQRCode />} />
              <Route path="/vendor/settings" element={<VendorSettings />} />
              <Route path="/vendor/plan" element={<VendorPlan />} />
              <Route path="/vendor/truck-info" element={<Suspense fallback={null}><VendorTruckInfo /></Suspense>} />
              <Route path="/vendor/birthday-card" element={<Suspense fallback={null}><VendorBirthdayCard /></Suspense>} />
              <Route path="/vendor/photos" element={<Suspense fallback={null}><VendorPhotos /></Suspense>} />
              <Route path="/vendor/menu" element={<Suspense fallback={null}><VendorMenu /></Suspense>} />
              <Route path="/vendor/social" element={<Suspense fallback={null}><VendorSocialMedia /></Suspense>} />
              <Route path="/vendor/preview" element={<Suspense fallback={null}><VendorProfilePreview /></Suspense>} />
              <Route path="/vendor/pos-select" element={<Suspense fallback={null}><VendorPOSSelection /></Suspense>} />
              <Route path="/vendor/portfolio" element={<Suspense fallback={null}><VendorPortfolio /></Suspense>} />
              <Route path="/vendor/smart-menu" element={<Suspense fallback={null}><VendorMenuScan /></Suspense>} />
              <Route path="/vendor/orders" element={<Suspense fallback={null}><VendorOrders /></Suspense>} />
              <Route path="/vendor/kitchen" element={<Suspense fallback={null}><KitchenDisplay /></Suspense>} />
              <Route path="/vendor/punch-card" element={<Suspense fallback={null}><VendorPunchCard /></Suspense>} />
              <Route path="/vendor/holiday-campaigns" element={<Suspense fallback={null}><VendorHolidayCampaigns /></Suspense>} />
              <Route path="/vendor/schedule" element={<Suspense fallback={null}><VendorSchedulePlanner /></Suspense>} />
              <Route path="/vendor/achievements" element={<Suspense fallback={null}><VendorAchievements /></Suspense>} />
              <Route path="/vendor/reviews" element={<Suspense fallback={null}><VendorReviews /></Suspense>} />
            </Route>
            {/* Redirect everything else to vendor dashboard */}
            <Route path="*" element={<Navigate to="/vendor" replace />} />
          </Routes>
        </BrowserRouter>
        <DevBadge />
        <Toaster position="top-center" richColors theme="dark" />
        <ErrorToast />
      </div>
      </ErrorBrainBoundary>
      </OfflineProvider>
    );
  }

  // Standard user mode
  return (
    <OfflineProvider>
    <ErrorBrainBoundary onError={showError}>
    <div className="max-w-md mx-auto min-h-screen shadow-2xl relative" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }}>
        {/* Pending vendor banner */}
        {user && isPending && !bannerDismissed && (
          <PendingVendorBanner onDismiss={() => setBannerDismissed(true)} />
        )}
        <OfflineBanner />
        {/* Mode switcher for verified vendors in user mode */}
        {user && isVerified && <ModeSwitcher />}
        <BrowserRouter>
          <ScrollRestore />
          <Routes>
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/fork" element={<ProtectedRoute><ForkScreen /></ProtectedRoute>} />
            <Route path="/onboarding/user" element={<ProtectedRoute><UserOnboarding /></ProtectedRoute>} />
            <Route path="/onboarding/vendor" element={<ProtectedRoute><VendorOnboarding /></ProtectedRoute>} />
            <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
            <Route path="/disclaimer" element={<ProtectedRoute><DisclaimerPage /></ProtectedRoute>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Public browse routes — guest accessible */}
            <Route path="/" element={<HomePage />} />
            <Route path="/countdown" element={<CountdownPage />} />
            <Route path="/offer/:businessId" element={<OfferDetailPage />} />
            <Route path="/birthday-offer/:offerId" element={<BirthdayOfferDetailPage />} />
            <Route path="/admin/coverage-map/fullscreen" element={<FullscreenCoverageMap />} />
            <Route path="/truck/:businessId" element={<FlagRoute flag="truck_features"><FoodTruckDetail /></FlagRoute>} />
            <Route path="/offer/:businessId/verifications" element={<VerificationsPage />} />
            <Route path="/city/:cityName" element={<CityDealsPage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/live-map" element={<FlagRoute flag="truck_features"><LiveTruckMap /></FlagRoute>} />
            <Route path="/trending-map" element={<TrendingMapPage />} />
            <Route path="/weather-report" element={<WeatherReportPage />} />
            <Route path="/truck-leaderboard" element={<FlagRoute flag="truck_features"><TruckLeaderboardPage /></FlagRoute>} />
            <Route path="/ethical-picks" element={<EthicalPicksPage />} />
            <Route path="/shared-itinerary/:shareId" element={<SharedItineraryPage />} />
            <Route path="/shared-wishlist/:shareId" element={<SharedWishlistPage />} />
            <Route path="/annual-report" element={<AnnualReportPage />} />
            <Route path="/birthday-plan/:planId" element={<BirthdayPlanViewPage />} />
            <Route path="/friend-countdown/:shareToken" element={<FriendCountdownPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/about" element={<AboutPage />} />
            {/* Backward compat redirects */}
            <Route path="/tracker" element={<Navigate to="/my-deals" replace />} />
            <Route path="/favorites" element={<Navigate to="/my-deals" replace />} />
            <Route path="/explore" element={<Navigate to="/" replace />} />
            <Route path="/top-picks" element={<Navigate to="/" replace />} />
            {/* Protected routes — require auth */}
            <Route path="/earn-points" element={<ProtectedRoute><EarnPointsPage /></ProtectedRoute>} />
            <Route path="/my-deals" element={<ProtectedRoute><MyDealsPage /></ProtectedRoute>} />
            <Route path="/discover" element={<DiscoverPage />} />
            <Route path="/my-birthday" element={<ProtectedRoute><MyBirthdayPage /></ProtectedRoute>} />
            <Route path="/activity" element={<ProtectedRoute><ActivityPage /></ProtectedRoute>} />
            <Route path="/wanted-promos" element={<ProtectedRoute><WantedPromosPage /></ProtectedRoute>} />
            <Route path="/spot-truck" element={<ProtectedRoute><FlagRoute flag="truck_features"><SpotTruckPage /></FlagRoute></ProtectedRoute>} />
            <Route path="/report-update" element={<ProtectedRoute><ReportUpdatePage /></ProtectedRoute>} />
            <Route path="/beta" element={<ProtectedRoute><BetaPanelPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
            {/* v1.0.8 Document 4 Step 6 — Decision Log table page */}
            <Route path="/admin/decision-log" element={<AdminRoute><DecisionLogTablePage /></AdminRoute>} />
            <Route path="/tips" element={<ProtectedRoute><TipsPage /></ProtectedRoute>} />
            <Route path="/my-analytics" element={<ProtectedRoute><UserAnalyticsPage /></ProtectedRoute>} />
            <Route path="/submit-offer" element={<ProtectedRoute><SubmitOfferPage /></ProtectedRoute>} />
            <Route path="/my-submissions" element={<ProtectedRoute><MySubmissionsPage /></ProtectedRoute>} />
            <Route path="/plan-birthday" element={<ProtectedRoute><PlanBirthdayPage /></ProtectedRoute>} />
            <Route path="/my-itinerary" element={<ProtectedRoute><ItineraryPage /></ProtectedRoute>} />
            <Route path="/wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
            <Route path="/whats-new" element={<ProtectedRoute><WhatsNewPage /></ProtectedRoute>} />
            <Route path="/my-decision" element={<ProtectedRoute><DecisionEnginePage /></ProtectedRoute>} />
            <Route path="/birthday-summary/:year" element={<ProtectedRoute><BirthdaySummaryPage /></ProtectedRoute>} />
            <Route path="/birthday-crawl" element={<ProtectedRoute><FlagRoute flag="truck_features"><BirthdayCrawlPlanner /></FlagRoute></ProtectedRoute>} />
            <Route path="/birthday-wrapped" element={<ProtectedRoute><BirthdayWrapped /></ProtectedRoute>} />
            <Route path="/birthday-wrapped/:reportId" element={<ProtectedRoute><BirthdayWrapped /></ProtectedRoute>} />
            <Route path="/wrapped-history" element={<ProtectedRoute><Suspense fallback={null}><BirthdayWrappedHistory /></Suspense></ProtectedRoute>} />
            <Route path="/truck/:businessId/order" element={<ProtectedRoute><FlagRoute flag="truck_features"><TruckOrderPage /></FlagRoute></ProtectedRoute>} />
            <Route path="/age-unlock" element={<ProtectedRoute><AgeUnlockScreen /></ProtectedRoute>} />
            <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
            <Route path="/concepts" element={<ProtectedRoute><ConceptsPage /></ProtectedRoute>} />
            <Route path="/onboarding-demo" element={<ProtectedRoute><OnboardingDemoPage /></ProtectedRoute>} />
            <Route path="/verification-history" element={<ProtectedRoute><VerificationHistoryPage /></ProtectedRoute>} />
            <Route path="/points-history" element={<ProtectedRoute><PointsHistoryPage /></ProtectedRoute>} />
            <Route path="/friends/birthdays" element={<ProtectedRoute><Suspense fallback={null}><BirthdayBuddy /></Suspense></ProtectedRoute>} />
            <Route path="/friends/birthdays/:friendId/plan" element={<ProtectedRoute><Suspense fallback={null}><FriendCrawlPlanner /></Suspense></ProtectedRoute>} />
            <Route path="/friends/referrals" element={<ProtectedRoute><Suspense fallback={null}><MyReferrals /></Suspense></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <AppTour />
          <AppNpsSurvey />
          <PostBirthdaySurveyBottomSheet />
          <FirstBadgeCelebration />
          <BetaBroadcastCard />
          <BetaFeedbackFAB />
        </BrowserRouter>
        <DevBadge />
        <InstallPromptBanner />
        <Toaster position="top-center" richColors />
        <ErrorToast />
      </div>
      </ErrorBrainBoundary>
      </OfflineProvider>
  );
}

export default App;
